import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * shadcn/ui Button, tuned to "The Thread" for the back office.
 *
 * Pills on web (design-spec hard rule 6). Colour carries meaning and is not decorative:
 *   default (sage)  — the affirming action: approve, save, sign in.
 *   coral           — the action that sends someone a message they did not ask for: reject,
 *                     suppress, un-suppress.
 *   gold            — SEAL/UNLOCK ONLY, per the design system. Exactly one thing in this console
 *                     qualifies: breaking glass on a transcript. If a second gold button ever
 *                     appears here, one of them is wrong.
 *   ghost/secondary — everything reversible.
 *
 * Every size clears the 44px minimum target except `sm`, which is for dense table rows on
 * pointer-first screens and is paired with generous row padding to keep the hit area honest.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-pill font-display font-medium leading-none transition-[color,background-color,border-color,filter] duration-ui ease-thread focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25 focus-visible:ring-offset-2 focus-visible:ring-offset-paper disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-sage-deep text-white shadow-btn-sage hover:bg-sage-hover',
        // The AA gap escalated here was RESOLVED by the founder on 16 Jul 2026: white on `coral`
        // is 3.30:1 and no token gets white to 4.5 on this hue, so **the label gives way, not the
        // brand**. The hue is exactly as signed off; `ink` on it is 4.92:1. This mirrors
        // referee-web deliberately — the reason this was escalated rather than re-toned locally
        // was to keep the two surfaces identical, so they move together or not at all.
        //
        // The hover inverts with the label: `coral-deep` was right under white (darker = more
        // contrast) but drops an ink label to 3.74:1, so coral now LIFTS like gold already did.
        // `filter` is not a colour — hence the explicit transition list above, or it would snap.
        // See design-spec/01-foundations.md, "The warm fills carry an `ink` label".
        coral: 'bg-coral text-ink shadow-btn-coral hover:brightness-105',
        // `ink`, not sage-deep: sage-deep on sage-tint measures 4.07:1, under AA. The tint still
        // reads as sage. (`sage-text` 4.62:1 would now be legal too, but `ink` is 13.6:1 here.)
        secondary: 'bg-sage-tint text-ink hover:bg-sage-tint/70',
        ghost: 'border border-line-2 bg-surface text-ink hover:bg-paper-2',
        // ink, not white: white on this gradient is 2.24-2.97:1 — the worst pairing in the
        // palette, and unrescuable by darkening (gold that dark stops being gold). ink is 5.46-7.24.
        gold: 'bg-gradient-to-br from-[#DAA43F] to-gold text-ink shadow-ready hover:brightness-105',
      },
      size: {
        default: 'h-11 px-6 text-[14.5px]',
        sm: 'h-9 px-4 text-[13.5px]',
        lg: 'h-12 px-8 text-[16px]',
        block: 'h-12 w-full px-6 text-[15px]',
        icon: 'h-11 w-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
